import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ArrowLeft,
  Globe,
  Calendar,
  Clock,
  BarChart3,
  Search,
  Play,
  Loader2,
  AlertTriangle,
  ExternalLink,
  TrendingUp,
  Crown,
  Users,
  Lightbulb,
  FlaskConical,
  Lock,
  Download,
  FileSpreadsheet,
  Sparkles,
  Palette,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSite, useSiteCrawls, useCreateSiteCrawl, useUpdateSite, useUpdateBranding, type Site, type SiteCrawl } from '../hooks/useSites'
import { SearchConsoleTab } from '@/client/modules/crawl/components/SearchConsoleTab'
import { GoogleAnalyticsTab } from '@/client/modules/crawl/components/GoogleAnalyticsTab'
import { BingWebmasterTab } from '@/client/modules/crawl/components/BingWebmasterTab'
import { RankTrackerTab } from '@/client/modules/crawl/components/RankTrackerTab'
import { BacklinksTab } from '@/client/modules/crawl/components/BacklinksTab'
import { TrendsTab } from '../components/TrendsTab'
import { ReportsSection } from '../components/ReportsSection'
import { CompetitorsTab } from '../components/CompetitorsTab'
import { DomainIntelTab } from '../components/DomainIntelTab'
import { KeywordResearchTab } from '../components/KeywordResearchTab'
import { useOpportunities } from '../hooks/useOpportunities'
import { useAiSummary, type AiActionItem } from '../hooks/useAiSummary'
import { InsightsTab } from '../components/InsightsTab'
import { ReportConfigDialog } from '../components/ReportConfigDialog'
import { ChatWidget } from '../components/ChatWidget'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { apiClient } from '@/client/lib/api-client'
import { useSession } from '@/client/lib/auth'
import { toast } from 'sonner'
import { scoreColor } from '@/client/lib/colors'

function formatDate(iso: string | null) {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function frequencyLabel(f: string | null) {
  if (!f) return 'None'
  return { daily: 'Daily', weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly' }[f] || f
}

function ScoreTrend({ scores }: { scores: number[] }) {
  const recent = scores.slice(-10)
  const max = Math.max(...recent, 1)
  return (
    <div className="flex items-end gap-1 h-32" title="SEO score trend (last 10 crawls)">
      {recent.map((s, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <span className="text-[10px] text-muted-foreground">{Math.round(s)}</span>
          <div
            className={`w-full rounded-sm ${s >= 80 ? 'bg-green-500/80' : s >= 50 ? 'bg-yellow-500/80' : 'bg-red-500/80'}`}
            style={{ height: `${(s / max) * 100}%` }}
            title={`Score: ${s}`}
          />
        </div>
      ))}
    </div>
  )
}

const IMPACT_STYLES: Record<string, string> = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
}

function AiSummarySection({ siteId }: { siteId: string }) {
  const { data, isLoading } = useAiSummary(siteId)

  if (isLoading) return <Skeleton className="h-48 w-full" />
  if (!data?.available || !data.items?.length) return null

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          AI Action Plan
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          5 prioritised actions based on your site's SEO data — refreshed every 24 hours
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {data.items.map((item: AiActionItem) => (
            <div key={item.priority} className="flex gap-3 items-start">
              <span className="shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                {item.priority}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium">{item.action}</span>
                  <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${IMPACT_STYLES[item.impact] ?? IMPACT_STYLES['low']}`}>
                    {item.impact}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{item.reason}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function OpportunitiesSection({ siteId }: { siteId: string }) {
  const { data, isLoading } = useOpportunities(siteId)
  const opportunities = data?.opportunities ?? []

  if (isLoading) return <Skeleton className="h-32 w-full" />
  if (opportunities.length === 0) return null

  function truncateUrl(url: string) {
    try {
      const u = new URL(url)
      const path = u.pathname + u.search
      return path.length > 50 ? path.slice(0, 47) + '...' : path
    } catch { return url }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          Quick Win Opportunities
        </CardTitle>
        <p className="text-xs text-muted-foreground">Pages ranking 4-20 with significant impressions — small improvements could boost rankings</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Page</TableHead>
                <TableHead className="text-right w-16">Pos</TableHead>
                <TableHead className="text-right w-24">Impressions</TableHead>
                <TableHead className="text-right w-16">Clicks</TableHead>
                <TableHead className="hidden sm:table-cell">Issues</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {opportunities.slice(0, 10).map((opp) => (
                <TableRow key={opp.url}>
                  <TableCell className="min-w-0">
                    <span className="text-xs truncate block" title={opp.url}>{truncateUrl(opp.url)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={opp.position <= 10 ? 'default' : 'secondary'} className="text-xs">
                      #{Math.round(opp.position)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm">{opp.impressions.toLocaleString()}</TableCell>
                  <TableCell className="text-right text-sm">{opp.clicks.toLocaleString()}</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {opp.flags.slice(0, 3).map((flag) => (
                        <Badge key={flag} variant="outline" className="text-[10px] px-1.5 py-0">
                          {flag}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

function OverviewContent({ siteId, site }: { siteId: string; site: Site }) {
  const { data: crawlsData, isLoading: crawlsLoading } = useSiteCrawls(siteId)
  const crawls = crawlsData?.crawls ?? []
  const completedCrawls = crawls.filter((c) => c.status === 'completed')
  const scores = completedCrawls
    .filter((c) => c.avg_seo_score != null)
    .map((c) => Number(c.avg_seo_score))
    .reverse()

  return (
    <div className="space-y-6">
      {/* Latest crawl summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Latest Score</p>
            <p className={`text-2xl font-bold ${
              (site.latest_avg_seo_score ?? 0) >= 80 ? 'text-green-600 dark:text-green-400' :
              (site.latest_avg_seo_score ?? 0) >= 50 ? 'text-yellow-600 dark:text-yellow-400' :
              'text-red-600 dark:text-red-400'
            }`}>
              {site.latest_avg_seo_score != null ? Math.round(site.latest_avg_seo_score) : '-'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Health</p>
            <p className={`text-2xl font-bold ${
              (site.latest_health_score ?? 0) >= 80 ? 'text-green-600 dark:text-green-400' :
              (site.latest_health_score ?? 0) >= 50 ? 'text-yellow-600 dark:text-yellow-400' :
              'text-red-600 dark:text-red-400'
            }`}>
              {site.latest_health_score != null ? `${site.latest_health_score}%` : '-'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Pages Crawled</p>
            <p className="text-2xl font-bold">{site.latest_pages_crawled ?? 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Issues</p>
            <p className="text-2xl font-bold">{site.latest_issues_count ?? 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total Crawls</p>
            <p className="text-2xl font-bold">{crawlsLoading ? '-' : crawls.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Score trend */}
      {scores.length >= 2 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">SEO Score Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ScoreTrend scores={scores} />
          </CardContent>
        </Card>
      )}

      {/* AI Executive Summary */}
      <AiSummarySection siteId={siteId} />

      {/* Opportunities (GSC + crawl data) */}
      <OpportunitiesSection siteId={siteId} />

      {/* Latest crawl link */}
      {site.latest_crawl_id && (
        <Card>
          <CardContent className="py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Latest Crawl</p>
              <p className="text-xs text-muted-foreground">{formatDate(site.latest_crawl_at)}</p>
            </div>
            <Link to={`/dashboard/crawls/${site.latest_crawl_id}`}>
              <Button size="sm" variant="outline">View Report</Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function HistoryContent({ siteId, latestCrawlId }: { siteId: string; latestCrawlId: string | null }) {
  const navigate = useNavigate()
  const { data, isLoading } = useSiteCrawls(siteId)
  const crawls = data?.crawls ?? []

  if (isLoading) return <Skeleton className="h-48 w-full" />

  if (crawls.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Clock className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No crawls yet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Start a crawl to see history here.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-1">
      {crawls.map((crawl: SiteCrawl) => (
        <div
          key={crawl.id}
          className="w-full flex items-center justify-between rounded-lg px-4 py-3 text-sm hover:bg-accent transition-colors"
        >
          <button
            onClick={() => navigate(`/dashboard/crawls/${crawl.id}`)}
            className="flex items-center gap-3 min-w-0 text-left"
          >
            <span className="text-muted-foreground">{formatDate(crawl.completed_at ?? crawl.created_at)}</span>
          </button>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-muted-foreground">{crawl.pages_crawled} pages</span>
            {crawl.issues_count > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {crawl.issues_count}
              </span>
            )}
            {crawl.avg_seo_score != null && (
              <span className={`text-xs font-medium ${scoreColor(crawl.avg_seo_score)}`}>
                {Math.round(crawl.avg_seo_score)}
              </span>
            )}
            <Badge variant={
              crawl.status === 'completed' ? 'outline' :
              crawl.status === 'running' ? 'default' :
              crawl.status === 'failed' ? 'destructive' : 'secondary'
            }>
              {crawl.status}
            </Badge>
            {crawl.status === 'completed' && latestCrawlId && crawl.id !== latestCrawlId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/dashboard/crawls/${latestCrawlId}?tab=compare&other=${crawl.id}`)
                }}
              >
                <BarChart3 className="mr-1 h-3 w-3" />
                Compare
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function BrandingCard({ siteId, site }: { siteId: string; site: Site }) {
  const updateBranding = useUpdateBranding(siteId)
  const [brandName, setBrandName] = useState(site.brand_name ?? '')
  const [brandLogoUrl, setBrandLogoUrl] = useState(site.brand_logo_url ?? '')
  const [brandColor, setBrandColor] = useState(site.brand_color ?? '')

  const handleSave = () => {
    updateBranding.mutate(
      { brandName: brandName || undefined, brandLogoUrl: brandLogoUrl || undefined, brandColor: brandColor || undefined },
      {
        onSuccess: () => toast.success('Branding saved'),
        onError: () => toast.error('Failed to save branding'),
      }
    )
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="space-y-0.5">
          <Label className="flex items-center gap-1.5">
            <Palette className="h-3.5 w-3.5" />
            Report Branding
          </Label>
          <p className="text-xs text-muted-foreground">
            Customise PDF and HTML reports with your brand. Append <code className="text-[10px] bg-muted px-1 rounded">?brand=site</code> to report URLs.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="brand-name">Brand Name</Label>
          <Input
            id="brand-name"
            placeholder="e.g. Jezweb SEO Report"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="brand-logo-url">Logo URL</Label>
          <Input
            id="brand-logo-url"
            placeholder="https://example.com/logo.svg"
            value={brandLogoUrl}
            onChange={(e) => setBrandLogoUrl(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="brand-color">Brand Colour</Label>
          <div className="flex gap-2 items-center">
            <Input
              id="brand-color"
              placeholder="#2563eb"
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              className="flex-1"
            />
            {brandColor && /^#[0-9a-fA-F]{6}$/.test(brandColor) && (
              <div className="h-8 w-8 rounded border shrink-0" style={{ backgroundColor: brandColor }} />
            )}
          </div>
        </div>

        <Button
          size="sm"
          onClick={handleSave}
          disabled={updateBranding.isPending}
        >
          {updateBranding.isPending ? 'Saving...' : 'Save Branding'}
        </Button>
      </CardContent>
    </Card>
  )
}

function ScheduleContent({ siteId, site }: { siteId: string; site: Site }) {
  const updateSite = useUpdateSite()
  const [alertEmail, setAlertEmail] = useState(site.alert_email ?? '')

  let config: Record<string, unknown> = {}
  try { config = site.schedule_config ? JSON.parse(site.schedule_config) : {} } catch { /* defaults */ }

  return (
    <div className="space-y-6 max-w-lg">
      {/* Report Builder */}
      <Card>
        <CardContent className="flex items-center justify-between pt-6">
          <div className="space-y-0.5">
            <Label>Report Builder</Label>
            <p className="text-xs text-muted-foreground">Generate custom reports with configurable sections.</p>
          </div>
          <ReportConfigDialog siteId={siteId} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 pt-6">
          {/* Enable/disable toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Schedule Enabled</Label>
              <p className="text-xs text-muted-foreground">
                Automatically crawl this site on a recurring schedule.
              </p>
            </div>
            <Switch
              checked={!!site.schedule_enabled}
              onCheckedChange={(checked) =>
                updateSite.mutate({ id: siteId, enabled: checked }, {
                  onSuccess: () => toast.success('Schedule updated'),
                  onError: () => toast.error('Failed to update schedule'),
                })
              }
            />
          </div>

          {/* Frequency */}
          <div className="space-y-2">
            <Label>Frequency</Label>
            <Select
              value={site.schedule_frequency ?? 'weekly'}
              onValueChange={(val) => updateSite.mutate({ id: siteId, frequency: val }, {
                onSuccess: () => toast.success('Schedule updated'),
                onError: () => toast.error('Failed to update schedule'),
              })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Schedule info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Next Run</p>
              <p className="text-xs">{formatDate(site.schedule_next_run_at)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Last Run</p>
              <p className="text-xs">{formatDate(site.schedule_last_run_at)}</p>
            </div>
          </div>

          {/* Crawl Config */}
          <div className="space-y-3 pt-2 border-t">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Crawl Settings</p>
            <div className="space-y-2">
              <Label>Max Pages</Label>
              <Input
                type="number"
                min={10}
                max={5000}
                defaultValue={(config['maxPages'] as number) ?? 500}
                onBlur={(e) => {
                  const val = Math.max(10, Math.min(5000, Number(e.target.value) || 500))
                  if (val !== ((config['maxPages'] as number) ?? 500)) {
                    updateSite.mutate({ id: siteId, maxPages: val }, {
                      onSuccess: () => toast.success('Max pages updated'),
                      onError: () => toast.error('Failed to update'),
                    })
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Rate Limit (requests/sec)</Label>
              <Input
                type="number"
                min={0.5}
                max={10}
                step={0.5}
                defaultValue={(config['rateLimit'] as number) ?? 2}
                onBlur={(e) => {
                  const val = Math.max(0.5, Math.min(10, Number(e.target.value) || 2))
                  if (val !== ((config['rateLimit'] as number) ?? 2)) {
                    updateSite.mutate({ id: siteId, rateLimit: val }, {
                      onSuccess: () => toast.success('Rate limit updated'),
                      onError: () => toast.error('Failed to update'),
                    })
                  }
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Respect robots.txt</Label>
                <p className="text-xs text-muted-foreground">Obey crawl restrictions from robots.txt.</p>
              </div>
              <Switch
                checked={!!(config['respectRobots'] as boolean)}
                onCheckedChange={(checked) =>
                  updateSite.mutate({ id: siteId, respectRobots: checked }, {
                    onSuccess: () => toast.success('Robots setting updated'),
                    onError: () => toast.error('Failed to update'),
                  })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Check external links</Label>
                <p className="text-xs text-muted-foreground">Verify external link status after crawling.</p>
              </div>
              <Switch
                checked={!!(config['checkExternalLinks'] as boolean)}
                onCheckedChange={(checked) =>
                  updateSite.mutate({ id: siteId, checkExternalLinks: checked }, {
                    onSuccess: () => toast.success('External link checking updated'),
                    onError: () => toast.error('Failed to update'),
                  })
                }
              />
            </div>
            {site.tier === 'managed' && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Collect Lighthouse</Label>
                  <p className="text-xs text-muted-foreground">Run Core Web Vitals audit on top 50 pages after crawl (~$0.21).</p>
                </div>
                <Switch
                  checked={!!(config['collectLighthouse'] as boolean)}
                  onCheckedChange={(checked) =>
                    updateSite.mutate({ id: siteId, collectLighthouse: checked }, {
                      onSuccess: () => toast.success('Lighthouse collection updated'),
                      onError: () => toast.error('Failed to update'),
                    })
                  }
                />
              </div>
            )}
          </div>

          {/* Alert email */}
          <div className="space-y-2">
            <Label>Alert Email</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="you@example.com"
                value={alertEmail}
                onChange={(e) => setAlertEmail(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateSite.mutate({ id: siteId, alertEmail: alertEmail || null }, {
                  onSuccess: () => toast.success('Schedule updated'),
                  onError: () => toast.error('Failed to update schedule'),
                })}
                disabled={updateSite.isPending}
              >
                Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Receive an email summary after each scheduled crawl.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Rank Check Schedule (managed tier only) */}
      {site.tier === 'managed' && (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-0.5">
              <Label className="flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5" />
                Rank Check Schedule
              </Label>
              <p className="text-xs text-muted-foreground">
                How often to check keyword rankings via DataForSEO.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select
                value={site.rank_check_frequency ?? 'weekly'}
                onValueChange={(val) => updateSite.mutate({ id: siteId, rankCheckFrequency: val }, {
                  onSuccess: () => toast.success('Rank check schedule updated'),
                  onError: () => toast.error('Failed to update'),
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm">
              <p className="text-muted-foreground">Next Check</p>
              <p className="text-xs">{formatDate(site.rank_check_next_at)}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Keyword Defaults (managed tier only) */}
      {site.tier === 'managed' && (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-0.5">
              <Label>Keyword Defaults</Label>
              <p className="text-xs text-muted-foreground">
                New keywords inherit these settings unless overridden.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Default Location</Label>
              <Input
                placeholder="Australia"
                defaultValue={site.default_keyword_location ?? 'Australia'}
                onBlur={(e) => {
                  const val = e.target.value.trim() || 'Australia'
                  if (val !== (site.default_keyword_location ?? 'Australia')) {
                    updateSite.mutate({ id: siteId, defaultKeywordLocation: val }, {
                      onSuccess: () => toast.success('Default location updated'),
                      onError: () => toast.error('Failed to update'),
                    })
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>Default Search Engine</Label>
              <Select
                value={site.default_keyword_engine ?? 'google'}
                onValueChange={(val) => updateSite.mutate({ id: siteId, defaultKeywordEngine: val }, {
                  onSuccess: () => toast.success('Default engine updated'),
                  onError: () => toast.error('Failed to update'),
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="google">Google</SelectItem>
                  <SelectItem value="bing">Bing</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Automated Reports (managed tier only) */}
      {site.tier === 'managed' && (
        <ReportsSection siteId={siteId} />
      )}

      {/* Report Branding */}
      <BrandingCard siteId={siteId} site={site} />
    </div>
  )
}

export function SiteDetailPage() {
  const { siteId } = useParams<{ siteId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'overview'
  const handleTabChange = (value: string) => {
    setSearchParams(value === 'overview' ? {} : { tab: value }, { replace: false })
  }
  const queryClient = useQueryClient()
  const { data: session } = useSession()
  const { data: site, isLoading } = useSite(siteId!)
  const createCrawl = useCreateSiteCrawl(siteId!)
  const updateSite = useUpdateSite()
  const isCreator = session?.user?.id === site?.created_by

  // Prefetch GSC/GA4 status on mount so tabs load instantly when clicked
  useEffect(() => {
    if (!siteId) return
    queryClient.prefetchQuery({
      queryKey: ['gsc', 'status', siteId],
      queryFn: () => apiClient.get(`/api/sites/${siteId}/gsc/status`),
      staleTime: 5 * 60 * 1000,
    })
    queryClient.prefetchQuery({
      queryKey: ['ga4', 'status', siteId],
      queryFn: () => apiClient.get(`/api/sites/${siteId}/ga4/status`),
      staleTime: 5 * 60 * 1000,
    })
  }, [siteId, queryClient])

  const handleStartCrawl = async () => {
    try {
      const result = await createCrawl.mutateAsync()
      navigate(`/dashboard/crawls/${result.id}`)
    } catch {
      toast.error('Failed to start crawl')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!site) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">Site not found.</p>
          <Link to="/dashboard/sites">
            <Button variant="outline" className="mt-4">Back to Sites</Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard/sites">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Globe className="h-5 w-5" />
              {site.domain}
            </h1>
            <p className="text-sm text-muted-foreground">{site.url}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isCreator && (
            <div className="flex items-center gap-2 mr-2">
              <Label htmlFor="private-toggle" className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1">
                <Lock className="h-3 w-3" />
                Private
              </Label>
              <Switch
                id="private-toggle"
                checked={!!site.is_private}
                onCheckedChange={(checked) =>
                  updateSite.mutate({ id: siteId!, isPrivate: checked }, {
                    onSuccess: () => toast.success(checked ? 'Site set to private' : 'Site shared with team'),
                    onError: () => toast.error('Failed to update privacy'),
                  })
                }
              />
            </div>
          )}
          <div className="flex items-center gap-2 mr-2">
            <Label htmlFor="tier-toggle" className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1">
              <Crown className="h-3 w-3" />
              Managed
            </Label>
            <Switch
              id="tier-toggle"
              checked={site.tier === 'managed'}
              onCheckedChange={(checked) =>
                updateSite.mutate({ id: siteId!, tier: checked ? 'managed' : 'free' }, {
                  onSuccess: () => toast.success(`Site ${checked ? 'upgraded to managed' : 'set to free'}`),
                  onError: () => toast.error('Failed to update tier'),
                })
              }
            />
          </div>
          {site.schedule_enabled ? (
            <Badge>
              <Calendar className="mr-1 h-3 w-3" />
              {frequencyLabel(site.schedule_frequency)}
            </Badge>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => window.open(`/api/sites/${siteId}/export?type=rankings&format=csv`, '_blank')}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Rankings (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/api/sites/${siteId}/export?type=gsc&format=csv`, '_blank')}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                GSC Snapshots (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/api/sites/${siteId}/export?type=ga4&format=csv`, '_blank')}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                GA4 Traffic (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/api/sites/${siteId}/export?type=backlinks&format=csv`, '_blank')}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Backlinks (CSV)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" onClick={handleStartCrawl} disabled={createCrawl.isPending}>
            {createCrawl.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Crawl Now
          </Button>
        </div>
      </div>

      {createCrawl.isError && (
        <p className="text-sm text-destructive">Failed to start crawl. Please try again.</p>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="flex flex-wrap gap-1 h-auto p-1 bg-muted/50">
          <TabsTrigger value="overview" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <Globe className="mr-1.5 h-3.5 w-3.5" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="insights" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            Insights
          </TabsTrigger>
          <TabsTrigger value="domain-intel" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <Lightbulb className="mr-1.5 h-3.5 w-3.5" />
            Domain Intel
          </TabsTrigger>
          <TabsTrigger value="research" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <FlaskConical className="mr-1.5 h-3.5 w-3.5" />
            Research
          </TabsTrigger>
          <TabsTrigger value="trends" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <TrendingUp className="mr-1.5 h-3.5 w-3.5" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="history" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <Clock className="mr-1.5 h-3.5 w-3.5" />
            History
          </TabsTrigger>
          <TabsTrigger value="schedule" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <Calendar className="mr-1.5 h-3.5 w-3.5" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="search-console" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <Search className="mr-1.5 h-3.5 w-3.5" />
            Search Console
          </TabsTrigger>
          <TabsTrigger value="analytics" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <BarChart3 className="mr-1.5 h-3.5 w-3.5" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="bing" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <Search className="mr-1.5 h-3.5 w-3.5" />
            Bing
          </TabsTrigger>
          <TabsTrigger value="rankings" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <TrendingUp className="mr-1.5 h-3.5 w-3.5" />
            Rankings
          </TabsTrigger>
          <TabsTrigger value="backlinks" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Backlinks
          </TabsTrigger>
          <TabsTrigger value="competitors" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
            <Users className="mr-1.5 h-3.5 w-3.5" />
            Competitors
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewContent siteId={siteId!} site={site} />
        </TabsContent>
        <TabsContent value="insights" className="mt-4">
          <InsightsTab siteId={siteId!} />
        </TabsContent>
        <TabsContent value="domain-intel" className="mt-4">
          <DomainIntelTab siteId={siteId!} tier={site.tier} />
        </TabsContent>
        <TabsContent value="research" className="mt-4">
          <KeywordResearchTab siteId={siteId!} tier={site.tier} />
        </TabsContent>
        <TabsContent value="trends" className="mt-4">
          <TrendsTab siteId={siteId!} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <HistoryContent siteId={siteId!} latestCrawlId={site.latest_crawl_id} />
        </TabsContent>
        <TabsContent value="schedule" className="mt-4">
          <ScheduleContent siteId={siteId!} site={site} />
        </TabsContent>
        <TabsContent value="search-console" className="mt-4">
          <SearchConsoleTab siteId={siteId!} domain={site.domain} tier={site.tier} />
        </TabsContent>
        <TabsContent value="analytics" className="mt-4">
          <GoogleAnalyticsTab siteId={siteId!} domain={site.domain} tier={site.tier} />
        </TabsContent>
        <TabsContent value="bing" className="mt-4">
          <BingWebmasterTab siteId={siteId!} domain={site.domain} tier={site.tier} />
        </TabsContent>
        <TabsContent value="rankings" className="mt-4">
          <RankTrackerTab
            siteId={siteId!}
            domain={site.domain}
            defaultLocation={site.default_keyword_location ?? undefined}
            defaultEngine={site.default_keyword_engine ?? undefined}
          />
        </TabsContent>
        <TabsContent value="backlinks" className="mt-4">
          <BacklinksTab siteId={siteId!} domain={site.domain} tier={site.tier} />
        </TabsContent>
        <TabsContent value="competitors" className="mt-4">
          <CompetitorsTab siteId={siteId!} domain={site.domain} tier={site.tier} />
        </TabsContent>
      </Tabs>

      {/* Conversational SEO Chat */}
      <ChatWidget siteId={siteId!} />
    </div>
  )
}
